import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTaskRuntime } from "../../context/TaskRuntimeContext";
import resolveAudioUrl from "../../../../utils/audio/resolveAudioUrl";

const auth = token => ({ headers: { Authorization: `Bearer ${token}` } });
const EMPTY_RESPONSES = Object.freeze([]);
const EMPTY_TRIALS = Object.freeze([]);
const AMBIGUITY_LINK_STORAGE_KEY = "byce-development-ambiguity-link";
const LEGACY_AMBIGUITY_LINK_STORAGE_KEY = "pvqr-development-ambiguity-link";

function responseForTrial(responses, trial) {
  return responses.find(response => String(response.trialId) === String(trial?.trialId)) || null;
}

function earliestReflectionResponse(responses) {
  return responses
    .filter(response => response.reflectionEvidence?.contractVersion === "2")
    .sort((left, right) => {
      const createdDifference = new Date(left.createdAt || 0) - new Date(right.createdAt || 0);
      return createdDifference || String(left._id || "").localeCompare(String(right._id || ""));
    })[0] || null;
}

function initialTrialIndex(trials, responses) {
  const lockedUnrevealed = trials.reduce((latest, trial, index) => {
    const response = responseForTrial(responses, trial);
    return response?.responseLifecycle?.state === "locked" && response.responseLifecycle.feedbackRevealed !== true
      ? index
      : latest;
  }, -1);
  if (lockedUnrevealed >= 0) return lockedUnrevealed;
  const unanswered = trials.findIndex(trial => responseForTrial(responses, trial)?.responseLifecycle?.state !== "locked");
  if (unanswered < 0) return Math.max(trials.length - 1, 0);
  if (unanswered > 0 && !trials[unanswered]?.firstOpenedAt) {
    const prior = responseForTrial(responses, trials[unanswered - 1]);
    if (prior?.responseLifecycle?.feedbackRevealed === true) return unanswered - 1;
  }
  return unanswered;
}

export default function useBYCETaskController() {
  const {
    session, step, program, task, responses, apiUrl, token, completeCurrentStep,
    navigateToProgramDashboard, navigateToProgramStep, refetchProgramState,
    temporalDelivery, participantTimeZone, programScopedLocale,
  } = useTaskRuntime();
  const stableResponses = responses || EMPTY_RESPONSES;
  const persistedReflectionResponse = earliestReflectionResponse(stableResponses);
  const persistedReflectionText = persistedReflectionResponse?.response?.text || "";
  const persistedReflectionTrialId = persistedReflectionResponse?.reflectionEvidence?.sourceTrialId
    ? String(persistedReflectionResponse.reflectionEvidence.sourceTrialId)
    : null;
  const trials = useMemo(() => session?.presentedTrials || EMPTY_TRIALS, [session?.presentedTrials]);
  const initiallyCompleted = useMemo(() => new Set(
    stableResponses.filter(response => response.responseLifecycle?.state === "locked")
      .map(response => String(response.trialId))
  ), [stableResponses]);
  const [completedTrialIds, setCompletedTrialIds] = useState(initiallyCompleted);
  const [currentIndex, setCurrentIndex] = useState(() => initialTrialIndex(trials, stableResponses));
  const trial = trials[currentIndex];
  const persistedTrialResponse = responseForTrial(stableResponses, trial);
  const currentResponseLocked = persistedTrialResponse?.responseLifecycle?.state === "locked";
  const firstPresentedTrial = trials[0];
  const stimulus = trial?.stimuli?.[0];
  const capabilities = task?.responseCapabilities || {};
  const config = task?.responseConfig || {};
  const [sample, setSample] = useState(null);
  const [anchors, setAnchors] = useState([]);
  const [value, setValue] = useState("");
  const [confidence, setConfidence] = useState(undefined);
  const [vectorValues, setVectorValues] = useState({});
  const [vectorConfidence, setVectorConfidence] = useState({});
  const [paired, setPaired] = useState({ currentConstructIndex: 0, currentScaleFamily: "categorical", vectorComplete: false });
  const [pairedValue, setPairedValue] = useState("");
  const [pairedConfidence, setPairedConfidence] = useState(undefined);
  const [feedback, setFeedback] = useState(null);
  const [text, setText] = useState(persistedReflectionText);
  const [reflectionContext, setReflectionContext] = useState(null);
  const [selectedReflectionTrialId, setSelectedReflectionTrialId] = useState(
    persistedReflectionTrialId
  );
  const [error, setError] = useState("");
  const [completionPresentation, setCompletionPresentation] = useState(null);
  const submissionInFlightRef = useRef(false);
  const resumeTransitionRef = useRef(null);
  const [submissionInFlight, setSubmissionInFlight] = useState(false);
  const reflectionCompletionInFlightRef = useRef(false);
  const acknowledgementRequirement = session?.effectiveRuntimeConfiguration?.step
    ?.processEvidence?.listeningConditionAcknowledgement;
  const acknowledgementRequired = acknowledgementRequirement?.required === true;
  const trialOpenRequired = session?.effectiveRuntimeConfiguration?.step
    ?.processEvidence?.trialFirstOpen?.required === true;
  const anchorTourRequired = session?.effectiveRuntimeConfiguration?.step
    ?.processEvidence?.anchorTour?.required === true;
  const sessionStartPresentation = session?.effectiveRuntimeConfiguration?.step
    ?.processEvidence?.sessionStartPresentation;
  const guidedIntroductionRequired = sessionStartPresentation?.requiredBeforeFirstTrial === true;
  const [listeningConditionsAcknowledged, setListeningConditionsAcknowledged] = useState(
    session?.listeningConditionAcknowledgement?.status === "acknowledged"
  );
  const [acknowledgementInFlight, setAcknowledgementInFlight] = useState(false);
  const [anchorTour, setAnchorTour] = useState(null);
  const [anchorTourDismissed, setAnchorTourDismissed] = useState(!anchorTourRequired);
  const [anchorTourInFlight, setAnchorTourInFlight] = useState(false);
  const [guidedIntroductionDismissed, setGuidedIntroductionDismissed] = useState(
    !guidedIntroductionRequired || Boolean(firstPresentedTrial?.firstOpenedAt)
  );
  const targetPlaybackGateRequired = capabilities.targetPlaybackEvidence?.submitGate?.required === true;
  const [targetPlaybackSatisfied, setTargetPlaybackSatisfied] = useState(!targetPlaybackGateRequired);
  const governedReflectionRequired = capabilities.reflectionEvidence?.contractVersion === "2";
  const reportFailure = useCallback((message, requestError) => {
    const serverMessage = requestError?.response?.data?.message || requestError?.response?.data?.error;
    const isMachineCode = typeof serverMessage === "string" && /^[A-Z][A-Z0-9_]+$/.test(serverMessage);
    setError(serverMessage && !isMachineCode ? serverMessage : message);
  }, []);

  useEffect(() => {
    if (!governedReflectionRequired || !session?._id) return;
    setReflectionContext(null);
    setText(persistedReflectionText);
    setSelectedReflectionTrialId(persistedReflectionTrialId);
    axios.get(`${apiUrl}/evaluation/session/${session._id}/reflection-context`, auth(token))
      .then(result => setReflectionContext(result.data.reflectionContext))
      .catch(requestError => reportFailure("The Reflection context could not be loaded.", requestError));
  }, [apiUrl, governedReflectionRequired, persistedReflectionText, persistedReflectionTrialId, reportFailure, session?._id, token]);

  useEffect(() => {
    setCompletedTrialIds(initiallyCompleted);
    setCurrentIndex(initialTrialIndex(trials, stableResponses));
  }, [initiallyCompleted, session?._id, stableResponses, trials]);

  useEffect(() => {
    setValue("");
    setConfidence(undefined);
    setVectorValues({});
    setVectorConfidence({});
    setFeedback(null);
    setError("");
  }, [trial?.trialId]);

  useEffect(() => {
    if (currentResponseLocked || !targetPlaybackGateRequired || !trial?.trialId || !stimulus?.stimulusInstanceId) {
      setTargetPlaybackSatisfied(!targetPlaybackGateRequired);
      return;
    }
    setTargetPlaybackSatisfied(false);
    axios.get(`${apiUrl}/evaluation/session/${session._id}/target-playbacks`, auth(token))
      .then(result => setTargetPlaybackSatisfied((result.data.targetPlaybackEvidence || []).some(event =>
        String(event.trialId) === String(trial.trialId) &&
        String(event.targetStimulusInstanceId) === String(stimulus.stimulusInstanceId)
      )))
      .catch(requestError => reportFailure("Target playback evidence could not be loaded.", requestError));
  }, [apiUrl, currentResponseLocked, reportFailure, session?._id, stimulus?.stimulusInstanceId, targetPlaybackGateRequired, token, trial?.trialId]);

  useEffect(() => {
    setListeningConditionsAcknowledged(
      session?.listeningConditionAcknowledgement?.status === "acknowledged"
    );
  }, [session?._id, session?.listeningConditionAcknowledgement?.status]);

  useEffect(() => {
    setAnchorTour(null);
    setAnchorTourDismissed(!anchorTourRequired);
  }, [anchorTourRequired, session?._id]);

  useEffect(() => {
    setGuidedIntroductionDismissed(
      !guidedIntroductionRequired || Boolean(firstPresentedTrial?.firstOpenedAt)
    );
  }, [firstPresentedTrial?.firstOpenedAt, firstPresentedTrial?.trialId, guidedIntroductionRequired, session?._id]);

  useEffect(() => {
    if (!anchorTourRequired || (acknowledgementRequired && !listeningConditionsAcknowledged)) return;
    axios.get(`${apiUrl}/evaluation/session/${session._id}/anchor-tour`, auth(token))
      .then(result => {
        const next = {
          ...result.data.anchorTour,
          anchors: (result.data.anchorTour?.anchors || []).map(anchor => ({ ...anchor, contentUrl: resolveAudioUrl(anchor.contentUrl) })),
        };
        setAnchorTour(next);
        if (next.completed) setAnchorTourDismissed(true);
      })
      .catch(requestError => reportFailure("The anchor tour could not be loaded.", requestError));
  }, [acknowledgementRequired, anchorTourRequired, apiUrl, listeningConditionsAcknowledged, reportFailure, session?._id, token]);

  useEffect(() => {
    if (currentResponseLocked || !trialOpenRequired || !trial?.trialId || trial.firstOpenedAt || (acknowledgementRequired && !listeningConditionsAcknowledged) || (anchorTourRequired && !anchorTourDismissed) || (guidedIntroductionRequired && !guidedIntroductionDismissed)) return;
    axios.post(
      `${apiUrl}/evaluation/session/${session._id}/trial/${trial.trialId}/open`,
      {},
      auth(token)
    ).catch(requestError => reportFailure("The Voice opening could not be recorded.", requestError));
  }, [acknowledgementRequired, anchorTourDismissed, anchorTourRequired, apiUrl, currentResponseLocked, guidedIntroductionDismissed, guidedIntroductionRequired, listeningConditionsAcknowledged, reportFailure, session?._id, token, trial?.firstOpenedAt, trial?.trialId, trialOpenRequired]);

  useEffect(() => {
    let active = true;
    if (!stimulus?.voiceSampleId) {
      setSample(null);
      return () => { active = false; };
    }
    const expectedVoiceSampleId = String(stimulus.voiceSampleId);
    setSample(null);
    axios.get(`${apiUrl}/voice-samples/${encodeURIComponent(expectedVoiceSampleId)}`)
      .then(result => {
        if (!active) return;
        const resolvedSample = result.data?.data;
        if (!resolvedSample || String(resolvedSample._id) !== expectedVoiceSampleId) {
          throw new Error("VOICE_SAMPLE_IDENTITY_MISMATCH");
        }
        setSample(resolvedSample);
      })
      .catch(() => {
        if (!active) return;
        setSample(null);
        setError("Could not load the provisional sample.");
      });
    return () => { active = false; };
  }, [apiUrl, stimulus?.voiceSampleId, trial?.trialId]);

  useEffect(() => {
    if (currentResponseLocked || !capabilities.referenceAnchors || !trial || (anchorTourRequired && !anchorTourDismissed)) return;
    setAnchors([]);
    axios.get(`${apiUrl}/evaluation/session/${session._id}/trial/${trial.trialId}/anchors`, auth(token))
      .then(result => setAnchors((result.data.anchors || []).map(anchor => ({
        ...anchor,
        contentUrl: resolveAudioUrl(anchor.contentUrl),
      }))))
      .catch(() => setError("Could not load reference anchors."));
  }, [anchorTourDismissed, anchorTourRequired, apiUrl, capabilities.referenceAnchors, currentResponseLocked, session?._id, token, trial]);

  const activeConstruct = useMemo(() => {
    if (capabilities.pairedScale) return capabilities.pairedScale.constructs?.[paired.currentConstructIndex]?.id;
    if (config.trialConstructPlan) return trial?.activeConstructId;
    return config.constructId;
  }, [capabilities.pairedScale, config.constructId, config.trialConstructPlan, paired.currentConstructIndex, trial?.activeConstructId]);
  const activeConstructConfig = useMemo(() => (
    config.trialConstructPlan
      ? config.constructs?.find(candidate => candidate.id === activeConstruct)
      : null
  ), [activeConstruct, config.constructs, config.trialConstructPlan]);

  const selectedReflectionCandidate = reflectionContext?.candidates?.find(candidate => candidate.sourceTrialId === selectedReflectionTrialId);
  const post = body => axios.post(`${apiUrl}/evaluation/response`, {
    sessionId: session._id,
    trialId: trial?.trialId || selectedReflectionCandidate?.sourceTrialId,
    trialKey: trial?.trialKey,
    stimulusInstanceId: stimulus?.stimulusInstanceId || selectedReflectionCandidate?.sourceStimulusInstanceId,
    responseType: task.responseType,
    ...body,
  }, auth(token));

  const acknowledgeListeningConditions = async () => {
    if (acknowledgementInFlight) return;
    setAcknowledgementInFlight(true);
    setError("");
    try {
      await axios.post(
        `${apiUrl}/evaluation/session/${session._id}/listening-condition-acknowledgement`,
        {},
        auth(token)
      );
      setListeningConditionsAcknowledged(true);
    } catch (requestError) {
      reportFailure("The listening-condition acknowledgement could not be recorded.", requestError);
    } finally {
      setAcknowledgementInFlight(false);
    }
  };

  const playAnchorTourItem = async anchor => {
    if (anchorTourInFlight) return;
    setAnchorTourInFlight(true);
    setError("");
    try {
      const result = await axios.post(
        `${apiUrl}/evaluation/session/${session._id}/anchor-tour/${anchor.anchorId}/play`,
        {},
        auth(token)
      );
      setAnchorTour({
        ...result.data.anchorTour,
        anchors: (result.data.anchorTour?.anchors || []).map(item => ({ ...item, contentUrl: resolveAudioUrl(item.contentUrl) })),
      });
    } catch (requestError) {
      reportFailure("The anchor-tour playback could not be recorded.", requestError);
    } finally {
      setAnchorTourInFlight(false);
    }
  };

  const advanceOrComplete = async () => {
    const nextCompleted = new Set(completedTrialIds);
    if (trial?.trialId) nextCompleted.add(String(trial.trialId));
    setCompletedTrialIds(nextCompleted);
    const nextIndex = currentIndex + 1;
    if (nextIndex < trials.length) {
      setCurrentIndex(nextIndex);
      return;
    }
    try {
      const composite = step?.learnerUnit?.presentation?.composite;
      if (composite?.role === "rating" && composite.reflectionStepKey) {
        await completeCurrentStep?.(session._id, { deferNavigation: true });
        const refreshed = await refetchProgramState?.();
        const pairedReflection = refreshed?.program?.steps?.find(candidate =>
          candidate.key === composite.reflectionStepKey);
        if (!pairedReflection) {
          throw new Error("PAIRED_REFLECTION_POST_COMPLETION_STATE_UNAVAILABLE");
        }
        navigateToProgramStep?.(composite.reflectionStepKey, {
          compositeBackStepKey: composite.sessionStepKey,
        });
        return;
      }
      const closing = step?.learnerUnit?.presentation?.completion;
      if (closing?.text) {
        await completeCurrentStep?.(session._id, { deferNavigation: true });
        setCompletionPresentation(closing);
      } else {
        await completeCurrentStep?.(session._id);
      }
    } catch (requestError) {
      reportFailure("The activity could not advance. Please try again.", requestError);
    }
  };

  const completeGovernedReflection = useCallback(async () => {
    const closing = step?.learnerUnit?.presentation?.completion;
    if (closing?.text) {
      await completeCurrentStep?.(session._id, { deferNavigation: true });
      setCompletionPresentation(closing);
    } else {
      await completeCurrentStep?.(session._id);
    }
  }, [completeCurrentStep, session?._id, step?.learnerUnit?.presentation?.completion]);

  useEffect(() => {
    if (!governedReflectionRequired || !persistedReflectionResponse || session?.isCompleted ||
        completionPresentation || reflectionCompletionInFlightRef.current) return;
    reflectionCompletionInFlightRef.current = true;
    completeGovernedReflection()
      .catch(requestError => reportFailure("The completed Reflection could not be reconciled. Please try again.", requestError))
      .finally(() => { reflectionCompletionInFlightRef.current = false; });
  }, [completeGovernedReflection, completionPresentation, governedReflectionRequired, persistedReflectionResponse, reportFailure, session?.isCompleted]);

  const revealFeedback = async () => {
    if (task.uiConfig?.referenceFeedbackEnabled !== true) return advanceOrComplete();
    const repeatedCapability = capabilities.repeatedJudgment;
    const trialPolicy = repeatedCapability?.trialPolicies?.find(policy =>
      policy.trialRole === trial.governedTrialRole);
    if (trialPolicy?.feedbackPolicy === "withheld") {
      setFeedback({ probeFirstPass: { blockId: trialPolicy.blockId, message: trialPolicy.message } });
      return;
    }
    const result = await axios.get(
      `${apiUrl}/evaluation/session/${session._id}/trial/${trial.trialId}/reference`, auth(token)
    );
    if (capabilities.ambiguityFeedback) {
      const ambiguity = await axios.get(
        `${apiUrl}/evaluation/session/${session._id}/trial/${trial.trialId}/ambiguity-feedback`, auth(token)
      );
      setFeedback(ambiguity.data.ambiguityFeedback);
      sessionStorage.setItem(AMBIGUITY_LINK_STORAGE_KEY, JSON.stringify({
        sourceSessionId: session._id,
        sourceTrialId: trial.trialId,
        feedbackProjectionVersion: ambiguity.data.ambiguityFeedback?.projectionVersion,
      }));
    } else {
      setFeedback({
        reference: result.data.reference,
        repeatedJudgmentMirror: result.data.repeatedJudgmentMirror || null,
      });
    }
  };

  const recoverLockedTransition = async () => {
    if (!currentResponseLocked || feedback) return;
    const key = `${session?._id}:${trial?.trialId}:${persistedTrialResponse?.responseLifecycle?.feedbackRevealed === true}`;
    resumeTransitionRef.current = key;
    setError("");
    try {
      await revealFeedback();
    } catch (requestError) {
      reportFailure("Saved feedback could not be recovered. Please try again.", requestError);
    }
  };

  useEffect(() => {
    if (!currentResponseLocked || feedback || !trial?.trialId) return;
    const key = `${session?._id}:${trial.trialId}:${persistedTrialResponse?.responseLifecycle?.feedbackRevealed === true}`;
    if (resumeTransitionRef.current === key) return;
    recoverLockedTransition();
  });

  const submitSimple = async () => {
    if (submissionInFlightRef.current) return;
    submissionInFlightRef.current = true;
    setSubmissionInFlight(true);
    setError("");
    try {
      const existingResponse = stableResponses.find(candidate =>
        String(candidate.trialId) === String(trial.trialId)
      );
      if (existingResponse?.responseLifecycle?.state !== "locked") {
        const constructId = activeConstructConfig?.id || config.constructId;
        const parameterId = activeConstructConfig?.parameterId || config.sourceParameterId || constructId;
        await post({
          response: {
            [constructId]: Number(value),
            ...(parameterId !== constructId
              ? { [parameterId]: Number(value) }
              : {}),
          },
          learnerConfidence: [{ constructId, scaleFamily: config.scaleFamily, value: confidence, primaryResponseTrialId: trial.trialId }],
          responseState: "locked",
        });
      }
      setCompletedTrialIds(current => new Set(current).add(String(trial.trialId)));
      await revealFeedback();
    } catch (requestError) {
      reportFailure("The rating could not be submitted. Please try again.", requestError);
    } finally {
      submissionInFlightRef.current = false;
      setSubmissionInFlight(false);
    }
  };

  const submitVector = async () => {
    if (submissionInFlightRef.current) return;
    submissionInFlightRef.current = true;
    setSubmissionInFlight(true);
    setError("");
    try {
      const constructs = config.constructs || [];
      await post({
        response: Object.fromEntries(constructs.map(construct => [construct.parameterId, Number(vectorValues[construct.id])])),
        learnerConfidence: constructs.map(construct => ({
          constructId: construct.id,
          scaleFamily: "continuous",
          value: vectorConfidence[construct.id],
          primaryResponseTrialId: trial.trialId,
        })),
        responseState: "locked",
      });
      setCompletedTrialIds(current => new Set(current).add(String(trial.trialId)));
      await revealFeedback();
    } catch (requestError) {
      reportFailure("The rating could not be submitted. Please try again.", requestError);
    } finally {
      submissionInFlightRef.current = false;
      setSubmissionInFlight(false);
    }
  };

  const pairedAction = async action => {
    setError("");
    try {
      const construct = capabilities.pairedScale.constructs[paired.currentConstructIndex];
      const scale = paired.currentScaleFamily;
      const result = await post({ pairedScaleAction: {
        type: action,
        constructId: construct.id,
        scaleFamily: scale,
        parameterId: construct[scale].parameterId,
        value: Number(pairedValue),
        confidenceValue: pairedConfidence,
      }});
      const next = result.data.response.pairedScale;
      setPaired(next);
      setPairedValue("");
      setPairedConfidence(undefined);
      if (next.vectorComplete) await revealFeedback();
    } catch (requestError) {
      reportFailure("The rating transition could not be completed. Please try again.", requestError);
    }
  };

  const replay = anchor => axios.post(
    `${apiUrl}/evaluation/session/${session._id}/trial/${trial.trialId}/anchors/${anchor.anchorId}/replay`,
    { constructId: anchor.constructId, role: anchor.role }, auth(token)
  ).catch(requestError => reportFailure("Anchor replay evidence could not be recorded.", requestError));

  const recordTargetPlay = () => {
    if (!capabilities.targetPlaybackEvidence) return;
    return axios.post(
      `${apiUrl}/evaluation/session/${session._id}/trial/${trial.trialId}/target-playbacks`,
      { stimulusInstanceId: stimulus.stimulusInstanceId, presentationContext: "rating-card" }, auth(token)
    ).then(result => {
      if (result.data?.playbackCount > 0) setTargetPlaybackSatisfied(true);
      return result;
    }).catch(requestError => reportFailure("Target playback evidence could not be recorded.", requestError));
  };

  const submitReflection = async () => {
    if (submissionInFlightRef.current) return;
    if (governedReflectionRequired) {
      if (!selectedReflectionTrialId) return setError("Select one Voice before submitting the Reflection.");
      submissionInFlightRef.current = true;
      setSubmissionInFlight(true);
      setError("");
      try {
        await post({ response: { text }, reflectionLinkage: { sourceTrialId: selectedReflectionTrialId } });
        await completeGovernedReflection();
      } catch (requestError) {
        reportFailure("The reflection could not be submitted. Please try again.", requestError);
      } finally {
        submissionInFlightRef.current = false;
        setSubmissionInFlight(false);
      }
      return;
    }
    const linkage = JSON.parse(
      sessionStorage.getItem(AMBIGUITY_LINK_STORAGE_KEY) ||
      sessionStorage.getItem(LEGACY_AMBIGUITY_LINK_STORAGE_KEY) ||
      "null"
    );
    if (!linkage) return setError("Complete the ambiguity example before the linked reflection.");
    submissionInFlightRef.current = true;
    setSubmissionInFlight(true);
    setError("");
    try {
      await post({ response: { text }, reflectionLinkage: linkage });
      await advanceOrComplete();
    } catch (requestError) {
      reportFailure("The reflection could not be submitted. Please try again.", requestError);
    } finally {
      submissionInFlightRef.current = false;
      setSubmissionInFlight(false);
    }
  };

  return {
    session, step, program, task, trial, stimulus, capabilities, config, activeConstructConfig, apiUrl, sample, anchors,
    temporalDelivery, participantTimeZone, programScopedLocale,
    currentIndex, totalTrials: trials.length,
    value, setValue, confidence, setConfidence, vectorValues, setVectorValues,
    vectorConfidence, setVectorConfidence, paired, pairedValue,
    setPairedValue, pairedConfidence, setPairedConfidence, feedback, text,
    setText, error, activeConstruct, replay, recordTargetPlay, complete: advanceOrComplete, submitSimple,
    pairedAction, submitReflection, submitVector,
    reflectionContext, selectedReflectionTrialId, setSelectedReflectionTrialId, governedReflectionRequired,
    submissionInFlight,
    completionPresentation,
    continueFromCompletionPresentation: navigateToProgramDashboard,
    acknowledgementRequired,
    acknowledgementRequirement,
    listeningConditionsAcknowledged,
    acknowledgementInFlight,
    acknowledgeListeningConditions,
    anchorTourRequired,
    anchorTour,
    anchorTourDismissed,
    anchorTourInFlight,
    playAnchorTourItem,
    continueFromAnchorTour: () => {
      if (anchorTour?.completed) setAnchorTourDismissed(true);
    },
    sessionStartPresentation,
    guidedIntroductionRequired,
    guidedIntroductionDismissed,
    continueFromGuidedIntroduction: () => setGuidedIntroductionDismissed(true),
    targetPlaybackGateRequired,
    targetPlaybackSatisfied,
    persistedTrialResponse,
    currentResponseLocked,
    lockedTransitionPending: currentResponseLocked && !feedback && !error,
    retryLockedTransition: recoverLockedTransition,
    canSubmitSimple: !submissionInFlight && value !== "" && value !== undefined && confidence !== undefined && targetPlaybackSatisfied,
    canSubmitVector: !submissionInFlight && targetPlaybackSatisfied && Boolean(config.constructs?.length) && config.constructs.every(construct =>
      vectorValues[construct.id] !== "" && vectorValues[construct.id] !== undefined &&
      vectorConfidence[construct.id] !== undefined
    ),
  };
}
